import { DEG, FT_TO_M, NM_TO_FT } from '../util/units.js'

/**
 * A file in `public/`, addressed relative to wherever the app is mounted.
 *
 * NOT `/models/...`. A leading slash means "the root of the origin", which is
 * only correct when the trainer owns the whole domain. Mount it under a path —
 * `example.com/sim/`, which is exactly what you do when bolting it onto an
 * existing site — and the aeroplane is fetched from `example.com/models/...`,
 * which does not exist. The glTF loader is deliberately non-fatal, so the 404
 * is swallowed and you get the blocky procedural airframe with no error and no
 * clue why.
 *
 * `BASE_URL` is Vite's, and with `base: './'` it comes out as `./` — resolved
 * against the document, so it is right at the root, right under a path, and
 * right from a file:// URL.
 *
 * One trap worth knowing: it resolves against the DOCUMENT, so the page must be
 * served at `/sim/` and not `/sim`. Every static host redirects the second to
 * the first, but if you put a proxy in front of one, make it keep the trailing
 * slash.
 */
function asset(path) {
  const base = import.meta.env?.BASE_URL ?? './'
  return `${base}${base.endsWith('/') ? '' : '/'}${path}`
}

/* ===================================================================== *
 *  SIMULATION                                                           *
 * ===================================================================== */

export const SIM = {
  /**
   * Fixed integration rate for the pattern.
   *
   * The path is commanded rather than simulated, so this is not about
   * numerical stability — it is about the pattern being identical on a 60 Hz
   * laptop and a 144 Hz monitor. You are graded on where the aeroplane is when
   * you press a key, and "where the aeroplane is" must not depend on the
   * display it is being drawn to.
   */
  RATE_HZ: 120,
  DT: 1 / 120,
  /** Never advance more than this much wall time in one animation frame. */
  MAX_SUBSTEPS: 12,
}

/* ===================================================================== *
 *  AIRCRAFT                                                             *
 * ===================================================================== */

export const AIRCRAFT = {
  MODEL: 'f-14b',

  /** On-speed approach AoA. Everything in the trainer is built around this. */
  ONSPEED_AOA_UNITS: 15.0,
  /** Indexer tolerance: inside this band the doughnut is lit, alone. */
  AOA_DONUT_BAND: 0.6,
  /** Outside this the chevron is solid rather than flashing. */
  AOA_CHEVRON_BAND: 2.2,

  /**
   * Vertical distance from the pilot's eye to the hook point when on-speed.
   * The ball is flown with the eye; this offset is what makes the hook — not
   * the eye — arrive at the 3 wire.
   *
   * Derived, not looked up: with the eye at (0, 1.02, -7.05) and the hook on
   * the FDM's own station 5.33 m aft, the 7 degrees of pitch the jet carries
   * on speed puts the hook 5.13 m — 16.8 ft — below the eye. Published F-14
   * figures are 17 to 18 ft, so this sits right at the bottom of that range,
   * and being internally consistent matters more here than matching a number
   * from a manual that describes slightly different geometry.
   */
  HOOK_TO_EYE_FT: 16.8,

  /** Structural / procedural geometry, metres. Used by the 3D model. */
  LENGTH_M: 19.1,
  SPAN_SPREAD_M: 19.55,
  SPAN_SWEPT_M: 11.65,
  HEIGHT_M: 4.88,

  /** Wing sweep endpoints, degrees from the perpendicular. */
  SWEEP_MIN_DEG: 20,
  SWEEP_MAX_DEG: 68,

  /** Fuel state the trainer resets to: a representative recovery weight. */
  RESET_FUEL_LBS: [2400, 2000, 1100, 1100],

  /**
   * Height of the aircraft's reference point above the deck when it is sitting
   * on its wheels, feet.
   *
   * The glidepath is flown in reference-point altitude and puts the origin
   * 10.6 ft up at the moment the hook touches, because the jet is 10 degrees
   * nose up. Parked, it is this. The difference is the derotation, and
   * assigning the parked figure the instant the wheels arrive drops the whole
   * aeroplane three metres in one frame.
   *
   * Derived from the model: the mains bottom out 2.47 m below the origin.
   * `npm test` checks the two agree.
   */
  GEAR_STATIC_HEIGHT_FT: 8.1,

  /**
   * Height of the pilot's eye above the aircraft's reference point at approach
   * attitude, feet. EYE_LOCAL in F14Model.js is (0, 1.02, -7.05) m; rotated by
   * the 7 degrees of pitch the jet carries on speed that is 1.87 m up. Kept as
   * a plain number here because F14Model imports this file and the cycle is
   * not worth the convenience — `npm test` checks the two agree.
   */
  EYE_ABOVE_ORIGIN_FT: 6.14,
}

/* ===================================================================== *
 *  CARRIER — Nimitz class                                               *
 *                                                                       *
 *  Two frames are in play and it is worth being explicit:               *
 *                                                                       *
 *  SHIP frame  : origin midships on the waterline centreline,           *
 *                +X forward, +Y starboard, +Z up.                       *
 *  DECK frame  : origin at the ramp (round-down) on the ANGLED deck      *
 *                centreline at deck level, +X forward along the angled   *
 *                deck, +Y right of it, +Z up.                            *
 *                                                                       *
 *  Every recovery computation — glideslope, lineup, wire capture —      *
 *  happens in the DECK frame. Nothing else should.                       *
 * ===================================================================== */

export const CARRIER = {
  NAME: 'CVN-73 GEORGE WASHINGTON',

  HULL_LENGTH_FT: 1092,
  HULL_BEAM_FT: 134,
  DECK_WIDTH_FT: 252,
  DECK_HEIGHT_FT: 63,
  DRAFT_FT: 37,

  /** Angled deck is 9 degrees to port of the ship's centreline. */
  ANGLE_DEG: 9.0,

  /**
   * Ramp position expressed in the SHIP frame, feet. The round-down sits well
   * to starboard of the centreline: that offset plus 9 degrees of deck angle
   * is what lets a 760 ft landing area fit inside a 252 ft wide deck.
   */
  RAMP_SHIP_X_FT: -545,
  RAMP_SHIP_Y_FT: 42,

  /** Usable landing area measured forward from the ramp. */
  LANDING_AREA_LENGTH_FT: 760,
  LANDING_AREA_WIDTH_FT: 110,

  /**
   * Cross-deck pendants, feet forward of the ramp. Target is the 3 wire.
   * Spacing is 40 ft, which is why half a degree of glideslope error at the
   * ramp is the difference between a 3 and a 2.
   */
  WIRES_FT: [156, 196, 236, 276],
  TARGET_WIRE: 3,

  /** Optical landing system glideslope. */
  GLIDESLOPE_DEG: 3.5,

  /**
   * Full-scale ball deflection. Beyond this much glideslope error the ball is
   * pegged at the top or bottom of the lens and stops telling you anything.
   */
  BALL_FULL_SCALE_DEG: 0.7,

  /** IFLOLS lens position in the DECK frame, feet. Port side, abeam the 4. */
  LENS_DECK_X_FT: 300,
  LENS_DECK_Y_FT: -62,
  LENS_DECK_Z_FT: 9,

  /**
   * Ship's motion. Base recovery course is chosen to put wind down the deck.
   *
   * 30 knots is not decoration. The approach turn takes about a minute, and
   * everything the ship travels in that minute becomes groove length — it is
   * the single biggest term in how long you get to fly the ball. At 25 knots
   * the same turn rolls you out inside half a mile and the groove is eleven
   * seconds, which is not a CASE 1, it is an emergency.
   */
  BASE_COURSE_DEG: 240,
  SPEED_KTS: 30,

  /** Starting position of the ship in the world ENU frame, metres. */
  START_EAST_M: 0,
  START_NORTH_M: 0,
}

/** Derived: angled deck heading offset in radians (negative = to port). */
export const DECK_ANGLE_RAD = -CARRIER.ANGLE_DEG * DEG

/** Derived: target touchdown point in the DECK frame, feet. */
export const TARGET_TOUCHDOWN_FT = CARRIER.WIRES_FT[CARRIER.TARGET_WIRE - 1]

/* ===================================================================== *
 *  ENVIRONMENT                                                          *
 * ===================================================================== */

export const ENV = {
  /** True wind. Combined with 30 kts of ship you get ~35 kts down the deck. */
  WIND_FROM_DEG: 240,
  WIND_KTS: 5,
  SEA_STATE: 2,
  /**
   * Light turbulence, 0 to 1.
   *
   * Not weather so much as honesty. A commanded path is a mathematical curve,
   * and the one thing a real aeroplane never flies is a mathematical curve —
   * so with this at zero every number in the trainer can be exactly right and
   * the result still reads as a model being dragged along a wire. At 1 the
   * wings are never quite level for two seconds together and the aeroplane is
   * visibly being HELD on the path rather than following one.
   *
   * It is deterministic (see rails/air.js), so turning it up does not make the
   * test suite flaky — the same gust arrives at the same second of every run.
   */
  TURBULENCE: 1,
  /** Time of day drives the sun vector and the sea colour. */
  SUN_ELEVATION_DEG: 34,
  SUN_AZIMUTH_DEG: 205,
  VISIBILITY_M: 22000,
}

/* ===================================================================== *
 *  CASE 1 PATTERN                                                       *
 *                                                                       *
 *  The numbers a Tomcat crew actually flies. These are the gates the     *
 *  trainer scores you against.                                           *
 * ===================================================================== */

export const PATTERN = {
  /** Initial: 3 nm astern, on the ship's heading. */
  INITIAL_RANGE_NM: 3.0,
  INITIAL_ALT_FT: 800,
  INITIAL_SPEED_KTS: 350,

  /** The break itself. */
  BREAK_ALT_FT: 800,
  BREAK_BANK_DEG: 60,

  /** Downwind. Gear, flaps, hook, on speed. */
  DOWNWIND_ALT_FT: 600,
  /**
   * Abeam distance, nautical miles.
   *
   * NAVAIR 01-F14AAP-1 fig 8-3 says "abeam position commence turn 1 to 1.2 nmi"
   * and this is 1.30, which needs justifying rather than hand-waving.
   *
   * The chart gives four numbers for the back half of the pattern, and at 30
   * knots of ship they cannot all be true at once:
   *
   *     abeam 1.0-1.2 nm | 450-500 ft at the 90 | glide slope at 3/4 mile |
   *     groove 15-18 s
   *
   * They are one geometric problem, not four independent limits. The abeam
   * distance sets the turn radius; the radius sets how long the turn takes; and
   * everything the carrier steams during the turn becomes groove length. Fly
   * the chart's abeam and the turn is over in 59 seconds, the ship makes under
   * 3,000 ft in that time, and you roll out a third of a mile astern with a
   * 13 second groove and the 90 four hundred feet up — missing three of the
   * four numbers to hit one.
   *
   * `scripts/sweep.mjs` flies the whole parameter space rather than arguing
   * about it. Of 960 combinations, 119 satisfy all six published limits, and
   * every one of them is at 1.24 nm or wider. This is the pick: 1.28 nm with
   * 23.5 degrees of bank gives a 16.5 second groove, 496 ft at the 90, 353 ft
   * on a centred ball at three quarters of a mile, and the hook in the 3 wire
   * 4 ft off the centreline.
   *
   * 1.28 is inside the general CV NATOPS abeam of 1.1 to 1.4 nm, and it misses
   * the F-14 chart's own figure by 500 ft — which is the one error in the list
   * you cannot see out of the window. The other three you look at directly:
   * the altitude at the 90, the ball at the intercept, and how long you have to
   * fly it. Being 0.08 nm wide on a distance judged by eye off a wingtip is the
   * cheapest of the four errors available.
   *
   * The aeroplane also arrives at the abeam still closing on this line from the
   * break, so the distance it is actually AT when the turn starts comes out
   * around 1.2 nm — inside the chart after all.
   */
  DOWNWIND_ABEAM_DISTANCE_NM: 1.28,
  DOWNWIND_ALT_TOL_FT: 100,

  /** The 180: abeam the LSO platform, starting the approach turn. */
  ABEAM_ALT_FT: 600,

  /**
   * The 90: halfway round, on the doughnut. The book says 450 to 500 feet, and
   * it says it because you are still ABOVE the glidepath here — the glideslope
   * is not intercepted until three quarters of a mile.
   */
  NINETY_ALT_FT: 480,
  NINETY_ALT_TOL_FT: 75,

  /**
   * Where the glidepath is joined: "intercept glide slope at approximately
   * 3/4 mile, 360 feet, on-speed".
   *
   * Those two numbers are the same number. On a 3.5 degree slope, an eye at
   * 360 ft is (360 - 63 - 16.8) / tan(3.5) = 4,580 ft from the touchdown
   * point, and 4,580 ft is 0.75 nm. The book is quoting one point on the
   * glideslope two ways, which is a useful check that the trainer's deck
   * geometry agrees with NAVAIR's.
   */
  GLIDESLOPE_INTERCEPT_FT: 4580,

  /** The altitude that goes with it: the other half of the same fact. */
  GLIDESLOPE_INTERCEPT_ALT_FT: 360,

  /** In the groove: wings level, on centreline, on ball. */
  GROOVE_LENGTH_NM: 0.75,
  /** Groove time worth flying: 15-18 seconds. Shorter is an OK-underline risk. */
  GROOVE_TIME_MIN_S: 15,
  GROOVE_TIME_MAX_S: 18,

  /** Below this range from the ramp, deviations are graded "in close". */
  IN_CLOSE_FT: 800,
  /** Below this, "at the ramp" — every call here hurts the grade most. */
  AT_RAMP_FT: 300,

  /** Wave-off gates. */
  WAVEOFF_LINEUP_FT: 30,
  WAVEOFF_BALL_HIGH: 1.15,
  WAVEOFF_BALL_LOW: -0.95,
}

/* ===================================================================== *
 *  EXTERNAL MODEL                                                       *
 * ===================================================================== */

export const AIRFRAME_MODEL = {
  /**
   * External model. Drop the .glb into public/models/ and it replaces the
   * procedural airframe.
   *
   * Currently configured for bohmerang's "F-14 Tomcat - Fighter Jet - Free"
   * from Sketchfab: 43.2k triangles, licensed CC Attribution-NonCommercial-
   * ShareAlike. The author states it may not be used in commercial work. The
   * `credit` line below is shown on screen because that licence requires
   * attribution — do not remove it while using this model.
   *
   * If this ever needs to run commercially, KOG_THORNS' "Grumman F-14B
   * Tomcat" is plain CC BY 4.0 and is the B model besides. Swap the filename
   * and the credit; nothing else changes.
   */
  gltfUrl: asset('models/f14-tomcat.glb'),

  /**
   * Sketchfab's own canonical attribution string, as given by the COPY CREDITS
   * button on the download dialog. This exact wording — title, author, licence
   * — is what the licence asks for, so it is worth using verbatim rather than
   * paraphrasing.
   */
  credit:
    '"F-14 Tomcat - Fighter Jet - Free" by bohmerang, ' +
    'licensed under CC Attribution-NonCommercial-ShareAlike',

  /**
   * Work out orientation, scale and position from the geometry itself rather
   * than making you tune three numbers by trial and error. See autoFit.js —
   * the interesting part is that it identifies the fuselage axis by SYMMETRY,
   * because on an F-14 the span and the length are within 3% of each other and
   * "longest axis wins" picks the wrong one about half the time.
   *
   * Set to false and the explicit values below are used instead.
   */
  autoFit: true,

  /** True F-14B length, metres. autoFit scales the model to match. */
  trueLengthM: 19.1,
  /**
   * Placement is derived from the pilot, not the nose. The design eye is fixed
   * at z = -7.05 (the ball and the grading are measured from it), so the model
   * is slid fore and aft until the eye sits this fraction of the way back from
   * the nose. About 28% on an F-14 — a few metres of radome, then the cockpit.
   *
   * This is what makes an arbitrary downloaded model land with the camera in
   * the right place regardless of where its author put the origin.
   */
  eyeZ: -7.05,
  /**
   * Where the design eye should sit along the model, as a fraction of its
   * length. 0.23, not the 0.28 you would guess from a side view.
   *
   * A Tomcat has TWO cockpits, and 0.28 of 19.1 m is 5.35 m aft of the nose —
   * which is neither of them. It is the bulkhead and canopy bow between the
   * pilot and the RIO, so the camera sat inside solid structure and the view
   * was a wall of grey.
   *
   * This is measured rather than guessed: src/scene/cockpitProbe.js walks the
   * forward fuselage looking for open volume under a canopy and finds both
   * seats — the pilot at 23% of the length, the RIO at 34%. The console prints
   * what it found at load, so a different model can be lined up by reading two
   * numbers instead of by trial and error.
   */
  eyeFractionFromNose: 0.23,
  /** Vertical bias, since a bounding box includes fins above and gear below. */
  verticalCentreY: 0.25,

  /** Used only when autoFit is false. */
  gltfOffset: [0, 0, 0],
  gltfScale: 1,
  gltfRotationDeg: [0, 0, 0],

  /** Applied on top of autoFit, for nudging. */
  nudge: [0, 0, 0],
}

/* ===================================================================== *
 *  COCKPIT                                                              *
 * ===================================================================== */

export const COCKPIT = {
  /**
   * Sit in the downloaded aeroplane rather than in a procedural stand-in.
   *
   * The bohmerang model has a real cockpit inside it — canopy bow, glareshield,
   * instrument panel, HUD combiner — and it is far better than anything that
   * can be assembled out of primitives. So the cockpit view puts the camera at
   * the design eye INSIDE that model and hides the procedural pit entirely.
   *
   * Measured from the model's own geometry: near the centreline the canopy
   * peaks at 1.42 m about 5.3 m aft of the nose, which is exactly where the
   * auto-fit puts the design eye station, and the eye ends up 0.6 m below that
   * peak. That is a pilot sitting in a cockpit, not a camera in a radome.
   *
   * Set to false to go back to the procedural cockpit — useful if you swap in
   * a model with no interior, since an exterior-only mesh viewed from inside
   * is just the world with a nose in front of it.
   */
  useModelInterior: true,

  /**
   * Seat adjustment, metres in the aircraft's own frame: right, up, forward is
   * negative Z. Moves the VIEW only.
   *
   * It deliberately does not move `eye`, which is where the ball is flown from
   * and where the whole recovery is measured. Real seats move; the datum the
   * LSO grades you against does not.
   *
   * WHY IT IS NOT ZERO
   * The design eye at (0, 1.02, -7.05) was chosen for the procedural cockpit.
   * The downloaded model's cockpit is a few centimetres off it, and the model
   * wins, because the model is what you are looking at.
   *
   * Measured off its geometry: the pilot's canopy peaks at 1.55 in the
   * aircraft frame, and a fighter pilot's eyes sit about 0.42 m under the
   * glass — close, because the whole point of that canopy is the view over the
   * nose. That puts the eye at 1.13, eleven centimetres above the procedural
   * design eye.
   *
   * Eleven centimetres does not sound like much. At the metre or so from your
   * eyes to the glareshield it is nine degrees of view angle, and nine degrees
   * is the difference between seeing the deck over the nose and seeing the top
   * of the instrument panel.
   *
   * If it still looks wrong, do not edit this by hand and reload. Num 8 and
   * Num 2 move the seat live and print the number to paste back here.
   *
   * NOW 0.21, NOT 0.11
   * At 0.11 the top of the glareshield sat about five degrees ABOVE the horizon,
   * which means you could not see the sea over the nose in level flight — and on
   * approach, where the jet carries nearly eight degrees of nose-up, it is worse
   * by exactly that much again. The correction is measured rather than nudged:
   * five degrees at the metre or so from eye to coaming is eight centimetres,
   * plus a little to actually see over it rather than merely along it. The
   * F-14's whole forward-view case is that you can see the deck over the nose on
   * the ball, so this is not a matter of taste.
   */
  seatOffsetM: [0, 0.21, 0],

  /**
   * Reference heights inside the model's cockpit, metres in the aircraft frame.
   * Measured by src/scene/cockpitProbe.js, kept here so `npm test` can check
   * that the seat offset above has not put the pilot's eyes through the canopy
   * or down behind the instrument panel.
   */
  DESIGN_EYE_Y: 1.02,
  CANOPY_PEAK_Y: 1.55,
  COAMING_Y: 0.95,

  /**
   * Procedural cockpit style, used only when useModelInterior is false or no
   * model loaded.
   *
   * 'minimal' — coaming, panel and HUD only. Nothing above the sightline.
   * 'framed'  — adds the canopy bow, windscreen cross-member and braces.
   *
   * Minimal is the default and it is a considered choice, not a shortcut. A
   * procedurally-built canopy bow is a black tube 63 degrees wide sitting a
   * metre from your eye; the real one carries rivets, wear, reflections and
   * depth that make the eye read it as structure and look past it.
   */
  style: 'minimal',

  /** Optional separate glTF cockpit, if you have one better than the airframe's. */
  gltfUrl: null,
  gltfOffset: [0, 0, 0],
  gltfScale: 1,
}

/** Metres-per-foot shortcuts used all over the scene code. */
export const FT = FT_TO_M
